import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { setFlyoutContent, openFlyoutV2 } from '@/reducers/flyout.slice'
import { CONVERSATION_VIEWS, type ConversationView } from './lib/constants'
import {
  type ConversationSummary,
  type ActiveView,
  type HavenLaunchContext,
  type HavenLaunchAction,
  type HavenEntryPoint,
  type MessageTab,
} from './lib/types'

interface ConversationsPage {
  conversations: ConversationSummary[]
  hasMore: boolean
}

export type ConversationViewStatus = 'idle' | 'loading' | 'loaded' | 'error'

export interface ConversationViewState {
  ids: number[]
  hasMore: boolean
  status: ConversationViewStatus
}

interface ConversationViewPage extends ConversationsPage {
  view: ConversationView
  offset: number
  needsAttentionCount?: number
}

interface ConversationViewReload extends ConversationsPage {
  view: ConversationView
  needsAttentionCount?: number
}

const emptyView = (): ConversationViewState => ({
  ids: [],
  hasMore: false,
  status: 'idle',
})

const upsertConversations = (
  current: ConversationSummary[],
  incoming: ConversationSummary[]
): ConversationSummary[] => {
  const byId = new Map(incoming.map((c) => [c.id, c]))
  const merged = current.map((c) => byId.get(c.id) ?? c)
  const known = new Set(merged.map((c) => c.id))
  return [...merged, ...incoming.filter((c) => !known.has(c.id))]
}

const emptyViews = (): Record<ConversationView, ConversationViewState> => ({
  [CONVERSATION_VIEWS.open]: emptyView(),
  [CONVERSATION_VIEWS.history]: emptyView(),
  [CONVERSATION_VIEWS.needsAttention]: emptyView(),
})

interface AdviserChatState {
  conversations: ConversationSummary[]
  conversationViews: Record<ConversationView, ConversationViewState>
  needsAttentionCount: number
  activeConversationId: number | null
  panelOpen: boolean
  activeView: ActiveView
  conversationsLoaded: boolean
  hasMoreConversations: boolean
  pendingLaunchContext: HavenLaunchContext | null
  pendingEntryPoint: HavenEntryPoint | null
  activeLaunchAction: HavenLaunchAction | null
  activeEntryPoint: HavenEntryPoint | null
  activeThreadId: number | null
  messagesListTab: MessageTab | null
}

const initialState: AdviserChatState = {
  conversations: [],
  conversationViews: emptyViews(),
  needsAttentionCount: 0,
  activeConversationId: null,
  panelOpen: false,
  activeView: 'chat',
  conversationsLoaded: false,
  hasMoreConversations: false,
  pendingLaunchContext: null,
  pendingEntryPoint: null,
  activeLaunchAction: null,
  activeEntryPoint: null,
  activeThreadId: null,
  messagesListTab: null,
}

export interface HavenLaunch {
  context: HavenLaunchContext
  entryPoint: HavenEntryPoint
}

const adviserChatSlice = createSlice({
  name: 'adviserChat',
  initialState,
  reducers: {
    togglePanel: (state) => {
      state.panelOpen = !state.panelOpen
    },
    closePanel: (state) => {
      state.panelOpen = false
      state.activeLaunchAction = null
      state.activeEntryPoint = null
      state.activeThreadId = null
    },
    openPanel: (state) => {
      state.panelOpen = true
    },
    setActiveView: (state, action: PayloadAction<ActiveView>) => {
      state.activeView = action.payload
      state.activeThreadId = null
    },
    openMessageThread: (state, action: PayloadAction<number>) => {
      state.activeThreadId = action.payload
    },
    closeMessageThread: (state) => {
      state.activeThreadId = null
    },
    messagesListTabChanged: (state, action: PayloadAction<MessageTab>) => {
      state.messagesListTab = action.payload
    },
    setActiveConversationId: (state, action: PayloadAction<number | null>) => {
      state.activeConversationId = action.payload
    },
    /** Replaces the first page and keeps the pages loaded after it. */
    setFirstConversationsPage: (
      state,
      action: PayloadAction<ConversationsPage>
    ) => {
      const { conversations, hasMore } = action.payload
      const ids = new Set(conversations.map((c) => c.id))
      const later = state.conversations.filter((c) => !ids.has(c.id))
      state.conversations = [...conversations, ...later]
      if (!later.length) state.hasMoreConversations = hasMore
      state.conversationsLoaded = true
    },
    appendConversations: (state, action: PayloadAction<ConversationsPage>) => {
      const seen = new Set(state.conversations.map((c) => c.id))
      state.conversations.push(
        ...action.payload.conversations.filter((c) => !seen.has(c.id))
      )
      state.hasMoreConversations = action.payload.hasMore
    },
    conversationViewPending: (
      state,
      action: PayloadAction<{ view: ConversationView }>
    ) => {
      state.conversationViews[action.payload.view].status = 'loading'
    },
    conversationViewFailed: (
      state,
      action: PayloadAction<{ view: ConversationView }>
    ) => {
      state.conversationViews[action.payload.view].status = 'error'
    },
    conversationViewPageLoaded: (
      state,
      action: PayloadAction<ConversationViewPage>
    ) => {
      const { view, offset, conversations, hasMore, needsAttentionCount } =
        action.payload
      state.conversations = upsertConversations(
        state.conversations,
        conversations
      )

      const page = conversations.map((c) => c.id)
      const viewState = state.conversationViews[view]
      viewState.ids =
        offset === 0
          ? page
          : [
              ...viewState.ids,
              ...page.filter((id) => !viewState.ids.includes(id)),
            ]
      viewState.hasMore = hasMore
      viewState.status = 'loaded'
      if (needsAttentionCount !== undefined) {
        state.needsAttentionCount = needsAttentionCount
      }
    },
    /** Replaces a view with every page the customer had loaded, re-read in order. */
    conversationViewReloaded: (
      state,
      action: PayloadAction<ConversationViewReload>
    ) => {
      const { view, conversations, hasMore, needsAttentionCount } =
        action.payload
      state.conversations = upsertConversations(
        state.conversations,
        conversations
      )

      const viewState = state.conversationViews[view]
      viewState.ids = conversations.map((c) => c.id)
      viewState.hasMore = hasMore
      viewState.status = 'loaded'
      if (needsAttentionCount !== undefined) {
        state.needsAttentionCount = needsAttentionCount
      }
    },
    removeConversation: (state, action: PayloadAction<number>) => {
      state.conversations = state.conversations.filter(
        (c) => c.id !== action.payload
      )
      for (const view of Object.values(state.conversationViews)) {
        view.ids = view.ids.filter((id) => id !== action.payload)
      }
      if (state.activeConversationId === action.payload) {
        state.activeConversationId = null
      }
    },
    startNewConversation: (state) => {
      state.activeConversationId = null
      state.activeView = 'chat'
      state.activeThreadId = null
      state.activeLaunchAction = null
      state.activeEntryPoint = null
    },
    launchQueued: (state, action: PayloadAction<HavenLaunch>) => {
      state.activeThreadId = null
      state.pendingLaunchContext = action.payload.context
      state.pendingEntryPoint = action.payload.entryPoint
      state.activeConversationId = null
      state.activeView = 'chat'
    },
    consumePendingLaunchContext: (state) => {
      if (!state.pendingLaunchContext) return
      state.activeLaunchAction =
        state.pendingLaunchContext.type === 'action'
          ? state.pendingLaunchContext.action
          : null
      state.activeEntryPoint = state.pendingEntryPoint
      state.pendingLaunchContext = null
      state.pendingEntryPoint = null
    },
    conversationResumed: (state) => {
      state.activeLaunchAction = null
      state.activeEntryPoint = null
    },
  },
  // Close the adviser panel synchronously whenever any flyout/sheet OPENS so
  // both state changes commit in a single render — preventing the sheet from
  // appearing on top of an open panel.
  // Note: these reducers fire on flyout-open actions only. Closing the adviser
  // panel via closePanel does NOT dispatch these flyout actions, so there is no
  // circular dependency.
  extraReducers: (builder) => {
    builder
      .addCase(setFlyoutContent, (state) => {
        state.panelOpen = false
        state.activeLaunchAction = null
        state.activeEntryPoint = null
      })
      .addCase(openFlyoutV2, (state) => {
        state.panelOpen = false
        state.activeLaunchAction = null
        state.activeEntryPoint = null
      })
  },
})

export default adviserChatSlice.reducer
export const {
  togglePanel,
  closePanel,
  openPanel,
  setActiveView,
  setActiveConversationId,
  setFirstConversationsPage,
  appendConversations,
  conversationViewPending,
  conversationViewFailed,
  conversationViewPageLoaded,
  conversationViewReloaded,
  removeConversation,
  startNewConversation,
  launchQueued,
  consumePendingLaunchContext,
  conversationResumed,
  openMessageThread,
  closeMessageThread,
  messagesListTabChanged,
} = adviserChatSlice.actions
